// Aturan gamifikasi (fungsi murni): XP, level, streak, misi harian, target pekan, lencana,
// stiker, kostum TAKA, dan tingkat penguasaan topik. Semua dihitung dari profil harian
// (lib/profil.js), jadi hasilnya sama di setiap perangkat setelah profil digabung.
import { tanggalLokal } from '../../lib/profil.js';

/* ---------------- tanggal ---------------- */

const keDate = t => { const [y, m, d] = t.split('-').map(Number); return new Date(y, m - 1, d); };
export const geserHari = (t, n) => { const d = keDate(t); d.setDate(d.getDate() + n); return tanggalLokal(d); };
/** Senin pekan ini (pekan Senin–Minggu). */
export const awalPekan = t => { const d = keDate(t); return geserHari(t, -((d.getDay() + 6) % 7)); };
export const hariPekan = t => Array.from({ length: 7 }, (_, i) => geserHari(awalPekan(t), i));

/* ---------------- aktivitas & streak ---------------- */

/** Hari dihitung aktif bila menyelesaikan satu ronde, satu modul, atau cukup banyak latihan. */
export const hariAktif = h => !!h && ((h.r || 0) >= 1 || Object.keys(h.mo || {}).length >= 1 || (h.s || 0) >= 5 || (h.l || 0) >= 5);
export const MAKS_PELINDUNG = 2;

/**
 * Streak dengan pelindung: setiap 7 hari berturut-turut memberi 1 pelindung (maks. 2).
 * Satu hari bolong memakai satu pelindung (streak tetap jalan). Hari ini yang belum aktif
 * tidak memutus streak.
 */
export function hitungStreak(profil, hariIni = tanggalLokal()) {
  const aktif = Object.keys(profil.hari).filter(t => hariAktif(profil.hari[t]) && t <= hariIni).sort();
  const beku = [];
  if (!aktif.length) return { streak: 0, terbaik: 0, pelindung: 0, beku, aktifHariIni: false };
  let streak = 0, terbaik = 0, pelindung = 0;
  for (let t = aktif[0]; t <= hariIni; t = geserHari(t, 1)) {
    if (hariAktif(profil.hari[t])) {
      streak++;
      if (streak % 7 === 0 && pelindung < MAKS_PELINDUNG) pelindung++;
    } else if (t !== hariIni) {
      if (pelindung > 0 && streak > 0) { pelindung--; beku.push(t); } else streak = 0;
    }
    terbaik = Math.max(terbaik, streak);
  }
  return { streak, terbaik, pelindung, beku, aktifHariIni: hariAktif(profil.hari[hariIni]) };
}

/* ---------------- misi harian ---------------- */

// Angka acak berbenih (sama untuk tanggal & nama yang sama di semua perangkat).
function benih(s) { let h = 2166136261; for (const c of s) h = Math.imul(h ^ c.charCodeAt(0), 16777619); return () => { h += 0x6D2B79F5; let t = h; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
const ambil = (rnd, a) => a[Math.floor(rnd() * a.length)];

const nilaiDi = (h, cek) => (h.n || []).filter(cek).length;
export const JENIS_MISI = {
  soal: { grup: 'latihan', ikon: '✏️', target: [10, 15, 20], teks: n => `Jawab ${n} soal`, nilai: h => h.s || 0 },
  'benar-mtk': { grup: 'latihan', mapel: 'matematika', ikon: '🔢', target: [5, 8, 10], teks: n => `Jawab benar ${n} soal Matematika`, nilai: h => h.bm || 0 },
  'benar-bi': { grup: 'latihan', mapel: 'bahasa-indonesia', ikon: '📖', target: [5, 8], teks: n => `Jawab benar ${n} soal Bahasa Indonesia`, nilai: h => h.bi || 0 },
  ronde70: { grup: 'mutu', ikon: '🎯', target: [1], teks: () => 'Selesaikan 1 ronde dengan nilai minimal 70', nilai: h => nilaiDi(h, x => x >= 70) },
  ronde90: { grup: 'mutu', ikon: '🌟', target: [1], teks: () => 'Selesaikan 1 ronde dengan nilai minimal 90', nilai: h => nilaiDi(h, x => x >= 90) },
  kombo: { grup: 'mutu', ikon: '⚡', target: [4, 5, 6], teks: n => `Jawab benar ${n} soal berturut-turut`, nilai: h => h.k || 0 },
  modul: { grup: 'variasi', ikon: '🧭', target: [1], teks: () => 'Selesaikan 1 modul belajar', nilai: h => Object.keys(h.mo || {}).length },
  langkah: { grup: 'variasi', ikon: '👣', target: [5, 8], teks: n => `Selesaikan ${n} langkah di menu belajar`, nilai: h => h.l || 0 },
  pembahasan: { grup: 'variasi', ikon: '💡', target: [1, 2], teks: n => `Ikuti pembahasan sampai selesai untuk ${n} soal yang salah`, nilai: h => h.p || 0 },
  saran: { grup: 'variasi', ikon: '🗺️', target: [1], teks: () => 'Latihan 1 topik dari Peta Kemampuan', nilai: h => h.sa || 0 },
  'dua-mapel': { grup: 'variasi', ikon: '⚖️', target: [2], butuhDua: true, teks: () => 'Latihan Matematika dan Bahasa Indonesia hari ini', nilai: h => (h.m || []).length },
};

/**
 * Tiga misi hari ini: satu latihan, satu mutu, satu variasi. Ditentukan dari tanggal dan nama.
 * @param {string[]} mapelAktif id mapel yang aktif (misi mapel lain tidak dipilih)
 */
export function misiHari(tanggal, slug, mapelAktif = ['matematika']) {
  const rnd = benih(`${tanggal}|${slug}`), dua = mapelAktif.length >= 2;
  const boleh = j => (!j.mapel || mapelAktif.includes(j.mapel)) && (!j.butuhDua || dua);
  return ['latihan', 'mutu', 'variasi'].map(grup => {
    const id = ambil(rnd, Object.keys(JENIS_MISI).filter(k => JENIS_MISI[k].grup === grup && boleh(JENIS_MISI[k])));
    const j = JENIS_MISI[id], target = ambil(rnd, j.target);
    return { id, ikon: j.ikon, target, teks: j.teks(target) };
  });
}

/** Misi hari itu beserta progresnya. */
export function statusMisi(profil, tanggal, slug, mapelAktif) {
  const h = profil.hari[tanggal] || {};
  const misi = misiHari(tanggal, slug, mapelAktif).map(m => { const v = JENIS_MISI[m.id].nilai(h); return { ...m, nilai: Math.min(v, m.target), selesai: v >= m.target }; });
  return { misi, semua: misi.every(m => m.selesai), petiDibuka: !!h.kt };
}

/* ---------------- target pekan ---------------- */

export const TARGET_PEKAN = 5;
export function statusPekan(profil, hariIni = tanggalLokal()) {
  const hari = hariPekan(hariIni), { beku } = hitungStreak(profil, hariIni);
  const aktif = hari.filter(t => hariAktif(profil.hari[t])).length;
  const kunci = hari[0], diklaim = hari.some(t => profil.hari[t]?.kw === kunci);
  return { kunci, hari: hari.map(t => ({ t, aktif: hariAktif(profil.hari[t]), beku: beku.includes(t), nanti: t > hariIni, hariIni: t === hariIni })), aktif, target: TARGET_PEKAN, tercapai: aktif >= TARGET_PEKAN, diklaim };
}

/* ---------------- XP & level ---------------- */

export const XP = { benar: 10, salah: 2, ronde: 20, nilai80: 30, nilai100: 50, modul: 40, bintang: 10, langkah: 2, pembahasan: 5, misi: 30, semuaMisi: 50, peti: 20, pekan: 150 };

/** XP satu hari. `misi` = status misi hari itu (opsional, dihitung bila ada slug). */
export function xpHari(h, misi) {
  if (!h) return 0;
  let x = (h.b || 0) * XP.benar + Math.max(0, (h.s || 0) - (h.b || 0)) * XP.salah + (h.r || 0) * XP.ronde;
  for (const n of h.n || []) x += (n >= 80 ? XP.nilai80 : 0) + (n === 100 ? XP.nilai100 : 0);
  for (const b of Object.values(h.mo || {})) x += XP.modul + b * XP.bintang;
  x += (h.l || 0) * XP.langkah + (h.p || 0) * XP.pembahasan;
  if (misi) { x += misi.filter(m => m.selesai).length * XP.misi; if (misi.every(m => m.selesai)) x += XP.semuaMisi; }
  if (h.kt) x += XP.peti;
  if (h.kw) x += XP.pekan;
  return x;
}

export function totalXP(profil, slug, mapelAktif) {
  return Object.entries(profil.hari).reduce((a, [t, h]) => a + xpHari(h, statusMisi(profil, t, slug, mapelAktif).misi), 0);
}

// XP untuk mencapai level L: 50 × L × (L − 1). Level 2 = 100, level 5 = 1.000, level 10 = 4.500.
export const xpLevel = L => 50 * L * (L - 1);
export function infoLevel(xp) {
  let L = 1; while (xpLevel(L + 1) <= xp) L++;
  const dari = xpLevel(L), ke = xpLevel(L + 1);
  return { level: L, xp, dari, ke, persen: Math.round(((xp - dari) / (ke - dari)) * 100), gelar: gelarLevel(L) };
}
const GELAR = [[1, 'Telur Pintar'], [2, 'Anak Burung'], [3, 'Burung Penasaran'], [5, 'Penjelajah Ilmu'], [7, 'Pemikir Cerdas'], [10, 'Bintang Kelas'], [13, 'Jagoan TKA'], [16, 'Ahli Muda'], [20, 'Profesor Cilik']];
export const gelarLevel = L => GELAR.filter(([l]) => L >= l).at(-1)[1];

/* ---------------- penguasaan topik (peta kemampuan) ---------------- */

export const TINGKAT_TOPIK = {
  belum: { nama: 'Belum dicoba', urut: 0 },
  mulai: { nama: 'Baru mulai', urut: 1 },
  perlu: { nama: 'Perlu latihan', urut: 2 },
  berkembang: { nama: 'Berkembang', urut: 3 },
  mahir: { nama: 'Mahir', urut: 4 },
  kuasai: { nama: 'Dikuasai', urut: 5 },
};
/** Tingkat penguasaan dari jumlah jawaban dan 10 jawaban terakhir. */
export function tingkatTopik(t) {
  if (!t?.n) return { id: 'belum', akurasi: null };
  const h = t.h || '', akurasi = h.length ? [...h].filter(c => c === '1').length / h.length : t.b / t.n;
  if (t.n < 3) return { id: 'mulai', akurasi };
  if (t.n >= 8 && akurasi >= 0.8) return { id: 'kuasai', akurasi };
  if (akurasi >= 0.7) return { id: 'mahir', akurasi };
  if (akurasi >= 0.5) return { id: 'berkembang', akurasi };
  return { id: 'perlu', akurasi };
}
export const jumlahDikuasai = (profil, mapel) => Object.values(profil.topik[mapel] || {}).filter(t => tingkatTopik(t).id === 'kuasai').length;

/* ---------------- statistik seumur hidup ---------------- */

export function statistik(profil, slug, mapelAktif, hariIni = tanggalLokal()) {
  const hs = Object.entries(profil.hari);
  const jum = k => hs.reduce((a, [, h]) => a + (h[k] || 0), 0);
  const modul = {}; for (const [, h] of hs) for (const [id, b] of Object.entries(h.mo || {})) modul[id] = Math.max(modul[id] || 0, b);
  const st = hitungStreak(profil, hariIni);
  let misi = 0, pekan = 0;
  for (const [t, h] of hs) { misi += statusMisi(profil, t, slug, mapelAktif).misi.filter(m => m.selesai).length; if (h.kw) pekan++; }
  return {
    soal: jum('s'), benar: jum('b'), ronde: jum('r'), langkah: jum('l'), pembahasan: jum('p'),
    nilai100: hs.reduce((a, [, h]) => a + (h.n || []).filter(n => n === 100).length, 0),
    kombo: Math.max(0, ...hs.map(([, h]) => h.k || 0)),
    modul: Object.keys(modul).length, bintang3: Object.values(modul).filter(b => b === 3).length,
    hariAktif: hs.filter(([, h]) => hariAktif(h)).length, duaMapel: hs.filter(([, h]) => (h.m || []).length >= 2).length,
    stiker: stikerDimiliki(profil).length, misi, pekan,
    streak: st.streak, streakTerbaik: st.terbaik,
    kuasaiMtk: jumlahDikuasai(profil, 'matematika'), kuasaiBI: jumlahDikuasai(profil, 'bahasa-indonesia'),
  };
}

/* ---------------- lencana ---------------- */

export const TINGKAT_LENCANA = ['', 'Perunggu', 'Perak', 'Emas'];
export const LENCANA = [
  { id: 'api', ikon: '🔥', nama: 'Api Semangat', ket: n => `Latihan ${n} hari berturut-turut`, nilai: s => s.streakTerbaik, batas: [3, 7, 30] },
  { id: 'rajin', ikon: '📅', nama: 'Rajin Berlatih', ket: n => `Aktif berlatih selama ${n} hari`, nilai: s => s.hariAktif, batas: [5, 20, 60] },
  { id: 'pekan', ikon: '🗓️', nama: 'Pekan Juara', ket: n => `Capai target pekan ${n} kali`, nilai: s => s.pekan, batas: [1, 4, 12] },
  { id: 'penjawab', ikon: '✅', nama: 'Penjawab Ulung', ket: n => `Jawab benar ${n} soal`, nilai: s => s.benar, batas: [50, 300, 1000] },
  { id: 'ronde', ikon: '🏁', nama: 'Pelari Maraton', ket: n => `Selesaikan ${n} ronde kuis`, nilai: s => s.ronde, batas: [5, 25, 100] },
  { id: 'sempurna', ikon: '🏆', nama: 'Nilai Sempurna', ket: n => `Dapat nilai 100 sebanyak ${n} kali`, nilai: s => s.nilai100, batas: [1, 5, 20] },
  { id: 'kombo', ikon: '⚡', nama: 'Kombo Kilat', ket: n => `Benar ${n} soal berturut-turut`, nilai: s => s.kombo, batas: [5, 10, 20] },
  { id: 'gigih', ikon: '💪', nama: 'Pantang Menyerah', ket: n => `Selesaikan pembahasan ${n} soal yang salah`, nilai: s => s.pembahasan, batas: [5, 25, 100] },
  { id: 'penjelajah', ikon: '🧭', nama: 'Penjelajah Modul', ket: n => `Selesaikan ${n} modul belajar`, nilai: s => s.modul, batas: [3, 10, 25] },
  { id: 'bintang', ikon: '⭐', nama: 'Bintang Tiga', ket: n => `Dapat 3 bintang di ${n} modul`, nilai: s => s.bintang3, batas: [1, 5, 15] },
  { id: 'misi', ikon: '🎯', nama: 'Pemburu Misi', ket: n => `Selesaikan ${n} misi harian`, nilai: s => s.misi, batas: [5, 30, 100] },
  { id: 'kolektor', ikon: '📒', nama: 'Kolektor Stiker', ket: n => `Kumpulkan ${n} stiker`, nilai: s => s.stiker, batas: [5, 12, 24] },
  { id: 'jago-mtk', ikon: '🔢', nama: 'Jago Matematika', ket: n => `Kuasai ${n} topik Matematika`, nilai: s => s.kuasaiMtk, batas: [3, 10, 20], mapel: 'matematika' },
  { id: 'pembaca', ikon: '📖', nama: 'Pembaca Hebat', ket: n => `Kuasai ${n} topik Bahasa Indonesia`, nilai: s => s.kuasaiBI, batas: [3, 10, 20], mapel: 'bahasa-indonesia' },
  { id: 'dua-sayap', ikon: '⚖️', nama: 'Dua Sayap', ket: n => `Latihan dua mapel di hari yang sama ${n} kali`, nilai: s => s.duaMapel, batas: [1, 5, 15], butuhDua: true },
];

/** Status semua lencana: tingkat yang sudah didapat (0–3) dan progres ke tingkat berikutnya. */
export function statusLencana(stat, mapelAktif = ['matematika']) {
  return LENCANA.filter(l => (!l.mapel || mapelAktif.includes(l.mapel)) && (!l.butuhDua || mapelAktif.length >= 2)).map(l => {
    const v = l.nilai(stat), tingkat = l.batas.filter(b => v >= b).length, berikut = l.batas[tingkat];
    return { ...l, v, tingkat, berikut, persen: berikut ? Math.round((v / berikut) * 100) : 100, ketBerikut: berikut ? l.ket(berikut) : l.ket(l.batas[2]) };
  });
}

/* ---------------- stiker hewan Nusantara ---------------- */

export const STIKER = [
  { id: 'komodo', ikon: '🦎', nama: 'Komodo', fakta: 'Kadal terbesar di dunia. Hidup di Pulau Komodo dan Rinca, Nusa Tenggara Timur.' },
  { id: 'orangutan', ikon: '🦧', nama: 'Orangutan', fakta: 'Kera besar berbulu cokelat kemerahan dari hutan Sumatra dan Kalimantan.' },
  { id: 'harimau', ikon: '🐅', nama: 'Harimau Sumatra', fakta: 'Harimau terkecil yang masih hidup di dunia. Hanya ada di Pulau Sumatra.' },
  { id: 'gajah', ikon: '🐘', nama: 'Gajah Sumatra', fakta: 'Gajah yang tinggal di hutan Sumatra. Belalainya bisa mengambil makanan dan air.' },
  { id: 'badak', ikon: '🦏', nama: 'Badak Jawa', fakta: 'Badak bercula satu yang kini hanya hidup di Taman Nasional Ujung Kulon, Banten.' },
  { id: 'jalak', ikon: '🐦', nama: 'Jalak Bali', fakta: 'Burung berbulu putih dengan kulit biru di sekitar mata. Satwa khas Pulau Bali.' },
  { id: 'cendrawasih', ikon: '🦚', nama: 'Cendrawasih', fakta: 'Dijuluki burung surga karena bulunya sangat indah. Banyak ditemukan di Papua.' },
  { id: 'elang', ikon: '🦅', nama: 'Elang Jawa', fakta: 'Elang berjambul dari hutan Pulau Jawa. Rupanya dianggap mirip burung Garuda.' },
  { id: 'anoa', ikon: '🐃', nama: 'Anoa', fakta: 'Kerbau kerdil yang hanya hidup di Sulawesi dan pulau-pulau kecil di sekitarnya.' },
  { id: 'babirusa', ikon: '🐗', nama: 'Babirusa', fakta: 'Taring babirusa jantan tumbuh menembus moncong dan melengkung ke atas. Asalnya dari Sulawesi.' },
  { id: 'tarsius', ikon: '🐵', nama: 'Tarsius', fakta: 'Primata kecil bermata sangat besar. Di Indonesia ada di Sulawesi, Kalimantan, dan Sumatra. Aktif mencari serangga di malam hari.' },
  { id: 'bekantan', ikon: '🐒', nama: 'Bekantan', fakta: 'Monyet berhidung panjang yang hidup di hutan bakau Kalimantan.' },
  { id: 'penyu', ikon: '🐢', nama: 'Penyu Hijau', fakta: 'Penyu betina kembali ke pantai berpasir untuk bertelur.' },
  { id: 'hiu-paus', ikon: '🦈', nama: 'Hiu Paus', fakta: 'Ikan terbesar di dunia. Makanannya justru hewan laut yang sangat kecil, yaitu plankton.' },
  { id: 'kakatua', ikon: '🦜', nama: 'Kakatua Jambul Kuning', fakta: 'Burung putih berjambul kuning yang pandai meniru suara.' },
  { id: 'kupu', ikon: '🦋', nama: 'Kupu-Kupu Sayap Burung', fakta: 'Salah satu kupu-kupu terbesar. Sayapnya lebar seperti sayap burung kecil.' },
  { id: 'rusa', ikon: '🦌', nama: 'Rusa Timor', fakta: 'Rusa yang banyak hidup di padang rumput Nusa Tenggara.' },
  { id: 'pesut', ikon: '🐬', nama: 'Pesut Mahakam', fakta: 'Lumba-lumba air tawar yang hidup di Sungai Mahakam, Kalimantan Timur.' },
  { id: 'kalong', ikon: '🦇', nama: 'Kalong', fakta: 'Kelelawar pemakan buah. Ia membantu menyebarkan biji tanaman di hutan.' },
  { id: 'maleo', ikon: '🐔', nama: 'Maleo', fakta: 'Burung dari Sulawesi yang mengubur telurnya di pasir atau tanah yang hangat.' },
  { id: 'kuda', ikon: '🐎', nama: 'Kuda Sumba', fakta: 'Kuda bertubuh kecil tetapi kuat dari Pulau Sumba.' },
  { id: 'arwana', ikon: '🐟', nama: 'Arwana', fakta: 'Ikan bersisik besar dan berkilau yang hidup di sungai Kalimantan dan Papua.' },
  { id: 'ketam', ikon: '🦀', nama: 'Ketam Kenari', fakta: 'Kepiting darat terbesar di dunia. Capitnya cukup kuat untuk membuka buah kelapa.' },
  { id: 'celepuk', ikon: '🦉', nama: 'Celepuk', fakta: 'Burung hantu kecil yang aktif di malam hari. Saudara jauh TAKA!' },
];
export const stikerDimiliki = profil => [...new Set(Object.values(profil.hari).flatMap(h => h.st || []))].filter(id => STIKER.some(s => s.id === id));

/** Stiker acak yang belum dimiliki (null bila koleksi sudah lengkap). */
export function pilihStiker(profil, acak = Math.random) {
  const punya = new Set(stikerDimiliki(profil)), sisa = STIKER.filter(s => !punya.has(s.id));
  return sisa.length ? sisa[Math.floor(acak() * sisa.length)] : null;
}

/* ---------------- kostum TAKA ---------------- */

export const KOSTUM = [
  { id: 'polos', nama: 'TAKA asli', level: 1 },
  { id: 'topi-pet', nama: 'Topi pet', level: 2 },
  { id: 'syal', nama: 'Syal hangat', level: 3 },
  { id: 'dasi-kupu', nama: 'Dasi kupu-kupu', level: 4 },
  { id: 'topi-pesta', nama: 'Topi pesta', level: 5 },
  { id: 'pita', nama: 'Pita cantik', level: 6 },
  { id: 'headphone', nama: 'Headphone', level: 8 },
  { id: 'topi-penjelajah', nama: 'Topi penjelajah', level: 10 },
  { id: 'jubah', nama: 'Jubah pahlawan', level: 12 },
  { id: 'mahkota', nama: 'Mahkota', level: 14 },
  { id: 'toga', nama: 'Topi toga', level: 16 },
  { id: 'ikat-kepala', nama: 'Ikat kepala semangat', lencana: ['api', 2] },
  { id: 'medali', nama: 'Medali juara', lencana: ['sempurna', 2] },
];
/** Kostum yang terbuka untuk level & lencana saat ini. */
export function kostumTerbuka(level, lencana) {
  const tk = Object.fromEntries(lencana.map(l => [l.id, l.tingkat]));
  return KOSTUM.map(k => ({ ...k, terbuka: k.lencana ? (tk[k.lencana[0]] || 0) >= k.lencana[1] : level >= k.level }));
}
